/**
 * PII classifier driven by pattern definitions.
 *
 * Browser-compatible — takes pattern entries as input (no file I/O).
 * Pattern data is provided by the backend via Inertia props.
 */

export const PII_ALLOW = 0;
export const PII_WARN = 1;
export const PII_BLOCK = 2;

export interface PatternEntry {
    name: string;
    level?: string;
    keywords: string[] | null;
    regexes: string[];
}

export interface Span {
    start: number;
    end: number;
    name: string;
}

export interface ClassifyResult {
    score: number;
    reason: string;
    warn_spans: Span[];
    error_spans: Span[];
}

interface PatternGroup {
    name: string;
    level: string | undefined;
    kwRe: RegExp | null;
    valPatterns: RegExp[];
}

function buildKeywordRegex(keywords: string[]): RegExp {
    const sorted = [...keywords].sort((a, b) => b.length - a.length);
    const alts = sorted.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    // allow sloppy e/s plurals to generalize:
    return new RegExp(`\\b(?:${alts.join("|")})e?s?\\b`, "i");
}

function buildGroup(entry: PatternEntry): PatternGroup {
    const kwRe = entry.keywords != null ? buildKeywordRegex(entry.keywords) : null;
    // Use global flag so exec() can iterate all matches. Patterns can opt into
    // case-insensitive matching with a `(?i)` prefix (Python's inline flag
    // syntax) — JS RegExp doesn't accept inline flags, so we strip the prefix
    // and pass `i` explicitly. Keeps the YAML source single-truth across the
    // Python and JS classifiers.
    const valPatterns = entry.regexes.map((r) => {
        let flags = "g";
        let pattern = r;
        if (pattern.startsWith("(?i)")) {
            flags += "i";
            pattern = pattern.slice(4);
        }
        return new RegExp(pattern, flags);
    });
    return { name: entry.name, level: entry.level, kwRe, valPatterns };
}

function collectSpans(group: PatternGroup, text: string): Span[] {
    const spans: Span[] = [];
    for (const re of group.valPatterns) {
        re.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(text)) !== null) {
            spans.push({ start: m.index, end: m.index + m[0].length, name: group.name });
            // Guard against zero-length matches.
            if (m[0].length === 0) {
                re.lastIndex++;
            }
        }
    }
    return spans;
}

function scoreGroup(group: PatternGroup, text: string): number {
    if (group.level === "allow") {
        return PII_ALLOW;
    }
    // If keywords are defined but none found in text, no match.
    if (group.kwRe !== null && !group.kwRe.test(text)) {
        return PII_ALLOW;
    }
    // Keywords matched (or group has no keyword gate). Use the configured level.
    if (group.level === "warn") {
        return PII_WARN;
    }
    return PII_BLOCK;
}

export function buildClassifier(entries: PatternEntry[]) {
    const groups = entries.map(buildGroup);

    return function classify(input: string): ClassifyResult {
        let maxScore = PII_ALLOW;
        const hits: string[] = [];
        const warnSpans: Span[] = [];
        const errorSpans: Span[] = [];

        for (const group of groups) {
            const level = scoreGroup(group, input);
            if (level === PII_ALLOW) {
                continue;
            }

            const spans = collectSpans(group, input);
            if (spans.length === 0) {
                continue;
            }

            hits.push(group.name);
            if (level > maxScore) {
                maxScore = level;
            }

            if (level === PII_WARN) {
                warnSpans.push(...spans);
            } else {
                errorSpans.push(...spans);
            }
        }

        // Sort spans by start position.
        warnSpans.sort((a, b) => a.start - b.start);
        errorSpans.sort((a, b) => a.start - b.start);

        return {
            score: maxScore,
            reason: hits.join("; "),
            warn_spans: warnSpans,
            error_spans: errorSpans,
        };
    };
}
